import { useHandler } from "@redotech/react-util/hook";
import { useTriggerLoad } from "@redotech/react-util/load";
import {
  CompensationMethodData,
  NewReturnItemData,
} from "@redotech/redo-customer-portal-shared/flow/util";
import { ProductVariantResponse } from "@redotech/redo-model/draft-registration";
import { DraftReturn } from "@redotech/redo-model/draft-return/draft-return";
import { RedoButton } from "@redotech/redo-web/arbiter-components/buttons/redo-button";
import { Divider } from "@redotech/redo-web/divider";
import { Flex } from "@redotech/redo-web/flex";
import { Text } from "@redotech/redo-web/text";
import { memo, useEffect, useMemo } from "react";
import {
  cleanResponses,
  getWarrantyRegistrationData,
  Response,
  UserInfo,
  useSubmitWarrantyRegistration,
} from "../use-submit-warranty-registration";
import * as submitWarrantyRegistration from "./submit-warranty-registration.module.css";

export const SubmitWarrantyRegistration = memo(
  function SubmitWarrantyRegistration({
    item,
    submit,
    draftReturn,
  }: {
    item: NewReturnItemData;
    draftReturn: DraftReturn;
    submit: (data: CompensationMethodData) => void;
  }) {
    const { submitAndNavigateToWarrantyRegistration } =
      useSubmitWarrantyRegistration({});

    const { responses, userInfo, optInMarketing, offeringSelection } =
      useMemo(() => {
        return getWarrantyRegistrationData(item);
      }, [item]);

    const [submitLoad, triggerSubmit] = useTriggerLoad(async () => {
      await submitAndNavigateToWarrantyRegistration({
        product: draftReturn.returnableItems[0].products[0],
        account: userInfo,
        responses: cleanResponses(responses),
        draftReturnId: draftReturn._id,
        notifyCustomer: true,
        optInMarketing,
      });
    });

    const handleSubmit = useHandler(() => {
      triggerSubmit();
    });

    // If the customer has selected an offering, skip to review page instead of review step
    useEffect(() => {
      if (offeringSelection) {
        submit({});
      }
    }, [offeringSelection, submit]);

    return (
      <Flex flexDirection="column" justify="space-between" w="full">
        <Flex flexDirection="column" gap="2xl" pb="4xl">
          <Flex flexDirection="column" gap="xs">
            <Text fontSize="lg" fontWeight="semibold">
              Review registration details
            </Text>
            <Text fontSize="sm">
              Review the registration details for this item.
            </Text>
          </Flex>
          <Divider />
          <RegistrationDetails responses={responses} userInfo={userInfo} />
        </Flex>
        <Flex flexDirection="column" gap="lg" w="full">
          <Divider />
          <Flex justify="flex-end" w="full">
            <RedoButton
              hierarchy="primary"
              onClick={handleSubmit}
              pending={submitLoad.pending}
              text="Submit and register"
            />
          </Flex>
        </Flex>
      </Flex>
    );
  },
);

export const RegistrationDetails = memo(function RegistrationDetails({
  responses,
  userInfo,
}: {
  responses: Response[] | ProductVariantResponse[];
  userInfo: UserInfo | undefined;
}) {
  if (!userInfo) {
    return null;
  }
  return (
    <Flex flexDirection="column" gap="2xl">
      {responses.map((response, index) => (
        <Flex flexDirection="column" gap="xs" key={index}>
          <Text fontSize="md" fontWeight="medium">
            {response.message}
          </Text>
          <ResponseValue value={response.value} />
        </Flex>
      ))}
      <Flex flexDirection="column" gap="xs">
        <Text fontSize="md" fontWeight="medium">
          Customer info
        </Text>
        <Flex flexDirection="column" gap="md">
          <Flex flexDirection="column" gap="none">
            <Text fontSize="sm" textColor="secondary">
              {`${userInfo.firstName} ${userInfo.lastName}`}
            </Text>
            <Text fontSize="sm" textColor="secondary">
              {userInfo.address.street1}
            </Text>
            <Text fontSize="sm" textColor="secondary">
              {userInfo.address.street2}
            </Text>
            <Text
              fontSize="sm"
              textColor="secondary"
            >{`${userInfo.address.city}, ${userInfo.address.state} ${userInfo.address.zip}`}</Text>
          </Flex>
          <Flex flexDirection="column" gap="none">
            <Text fontSize="sm" textColor="secondary">
              {userInfo.email}
            </Text>
          </Flex>
        </Flex>
      </Flex>
    </Flex>
  );
});

const ResponseValue = memo(function ResponseValue({
  value,
}: {
  value: string | string[] | URL[];
}) {
  if (Array.isArray(value)) {
    return (
      <Flex flexDirection="column" gap="xs">
        {value.map((v, index) => {
          if (URL.canParse(v)) {
            return (
              <Flex
                borderColor="secondary"
                borderStyle="solid"
                borderWidth="1px"
                className={submitWarrantyRegistration.productImage}
                justify="center"
                key={index}
                radius="md"
              >
                <img
                  alt="Response"
                  src={v.toString()}
                  style={{ objectFit: "contain", borderRadius: "4px" }}
                  width="100%"
                />
              </Flex>
            );
          }
          return (
            <Text fontSize="sm" key={index} textColor="secondary">
              {v.toString()}
            </Text>
          );
        })}
      </Flex>
    );
  }
  return (
    <Text fontSize="sm" textColor="secondary">
      {value}
    </Text>
  );
});
